import { useCallback, useRef, useState } from 'react';
import { TabPanel, Tabs } from 'react-tabs';

import { OptionsBlock } from '@/lib/widgets/catalog';
import { CheckboxBlock } from '@/lib/widgets/catalog';
import { SaleCatalogFilterModal } from '@/lib/widgets/sale-catalog/sale-catalog-filter-modal';

import { SALE_OPTIONS } from '@/lib/entities/catalog/constants';
import { getTranslatedOptionsFn } from '@/lib/entities/catalog/lib/get-translated-options';
import { useCurrency } from '@/lib/entities/currency';
import { useSaleQuery } from '@/lib/entities/sale/api';
import { useSaleCatalogQuery } from '@/lib/entities/sale/lib';

import {
  Button,
  CircleButton,
  ToggleButton,
} from '@/lib/shared/components/Buttons';
import {
  RenderTabList,
  useTabListState,
} from '@/lib/shared/components/Gallery/GalleryModal';
import { modal } from '@/lib/shared/components/Modal';
import { P2 } from '@/lib/shared/components/Typography';
import { useWindowSize } from '@/lib/shared/hooks/use-window-size';
import { ArrowIcon } from '@/lib/shared/icons';
import { getWordEnding } from '@/lib/shared/utils/get-word-ending';

import { clsx } from 'clsx';
import { useTranslation } from 'next-i18next';

import styles from './sale-catalog-map.module.scss';

import { PhuketMapWithBeaches } from '../../catalog/phuket-map-with-beaches';
import { SamuiMapWithBeaches } from '../../catalog/samui-map-with-beaches';

const highlightCheckbox = (
  id: string,
  method: 'set' | 'remove',
  isSamui?: boolean
) => {
  const mapDistrict = document.querySelector('path#' + id) as HTMLElement;
  const additionalDistricts = [];
  if (id === 'BANG_TAO') {
    additionalDistricts.push(
      document.querySelector('path#LAGOON') as HTMLElement
    );
    additionalDistricts.push(
      document.querySelector('path#LAYAN') as HTMLElement
    );
  } else if (id === 'RAWAI') {
    additionalDistricts.push(
      document.querySelector('path#NAI_HARN') as HTMLElement
    );
  } else if (id === 'KARON') {
    additionalDistricts.push(
      document.querySelector('path#KATA') as HTMLElement
    );
    additionalDistricts.push(
      document.querySelector('path#KATA_NOI') as HTMLElement
    );
  }
  const districts = [mapDistrict, ...additionalDistricts];
  districts.forEach((d) => {
    if (!d) return;
    if (method === 'set') {
      d.style.fill = `url("#blueGradient${isSamui ? 'Samui' : ''}FilterMap")`;
    } else {
      d.style.removeProperty('fill');
    }
  });
};
type Props = {
  isInAllFilters?: boolean;
  includeSelectAll?: boolean;
  isMainPage?: boolean;
};
export const SaleCatalogMap = ({
  isInAllFilters,
  includeSelectAll,
  isMainPage,
}: Props) => {
  const {
    currency: { label: currency },
  } = useCurrency();
  const [isShow, setIsShow] = useState(false);
  const {
    t,
    i18n: { language },
  } = useTranslation('index');

  const { query } = useSaleCatalogQuery();

  const catalog = useSaleQuery(query, currency);
  const data = catalog.data;
  const defaultIndex = query.samuiDistrict && !query.phuketDistrict ? 1 : 0;

  const { width: windowWidth } = useWindowSize();
  const isTabAnimated = useRef(false);
  const [selected, setSelected] = useState(defaultIndex);
  const tabHeaders = [t('locations.PHUKET'), t('locations.SAMUI')];
  const tabListState = useTabListState({
    tabHeaders,
    isTabAnimated,
    windowWidth,
    selected,
  });

  const onSelect = useCallback((index: number) => {
    setSelected(index);
  }, []);

  const toggleShowBeaches = () => {
    setIsShow((s) => !s);
  };
  if (!data) return null;
  const { fieldsMeta, meta } = data;
  const getTranslatedOptions = getTranslatedOptionsFn(fieldsMeta, t);
  const quantity = (meta.totalElements as number) || 0;

  return (
    <Tabs
      tabIndex={selected}
      defaultIndex={defaultIndex}
      onSelect={onSelect}
      forceRenderTabPanel={false}
      className={clsx(
        styles.map,
        isInAllFilters && styles['map--all'],
        includeSelectAll && styles['map--selectAll']
      )}
    >
      {!isInAllFilters && (
        <div className={styles.map__top}>
          <CircleButton
            name="close modal"
            as={'button'}
            onClick={() =>
              modal.open(<SaleCatalogFilterModal />, { isFilterModal: true })
            }
            content={<ArrowIcon />}
            theme="mixed"
            className={styles['map__back--mobile']}
          />
          <P2>{t('islandDistrictAndBeach')}</P2>
        </div>
      )}
      <div
        className={clsx(
          styles.map__content,
          isInAllFilters && styles['map__content--all'],
          selected === 1 && styles['map__content--samui']
        )}
      >
        <div className={styles.map__left}>
          {RenderTabList({
            tabHeaders,
            isTabAnimated,
            theme: 'dark',
            ...tabListState,
          })}
          <TabPanel className={styles.tabPanel}>
            <CheckboxBlock
              className={clsx(
                styles.map__form,
                isInAllFilters && styles['map__form--all']
              )}
              onMouseEnter={(id) => highlightCheckbox(id, 'set')}
              onMouseLeave={(id) => highlightCheckbox(id, 'remove')}
              items={getTranslatedOptions(
                SALE_OPTIONS.phuketDistrict,
                'phuketDistrict',
                'locations.'
              )}
              isTwoColumns
              catalogQuery="phuketDistrict"
              {...((isInAllFilters || includeSelectAll) && {
                selectAllCheckbox: { label: t('catalog.wholeIsland') },
              })}
              type="sale"
            />
          </TabPanel>
          <TabPanel className={styles.tabPanel}>
            <CheckboxBlock
              className={styles.map__form}
              items={getTranslatedOptions(
                SALE_OPTIONS.samuiDistrict,
                'samuiDistrict',
                'locations.'
              )}
              onMouseEnter={(id) => highlightCheckbox(id, 'set', true)}
              onMouseLeave={(id) => highlightCheckbox(id, 'remove', true)}
              isTwoColumns
              catalogQuery="samuiDistrict"
              {...((isInAllFilters || includeSelectAll) && {
                selectAllCheckbox: { label: t('catalog.wholeIsland') },
              })}
              type="sale"
            />
          </TabPanel>
          {includeSelectAll && (
            <div className={styles.map__beaches}>
              <OptionsBlock
                items={getTranslatedOptions(
                  SALE_OPTIONS.phuketBeaches,
                  'phuketBeaches'
                )}
                catalogQuery="phuketBeaches"
                title={t('phuketBeaches')}
                type="sale"
              />
              <OptionsBlock
                items={getTranslatedOptions(
                  SALE_OPTIONS.samuiBeaches,
                  'samuiBeaches'
                )}
                catalogQuery="samuiBeaches"
                title={t('samuiBeaches')}
                type="sale"
              />
            </div>
          )}
          {!isInAllFilters && !includeSelectAll && !isMainPage && (
            <Button
              as="button"
              className={styles.map__button}
              size="lg"
              theme="dark"
              disabled={quantity === 0}
              label={`${t('show')} ${quantity} ${t(
                getWordEnding(
                  quantity,
                  [
                    'declinable.offer',
                    'declinable.offers',
                    'declinable.offersMore',
                  ],
                  language as 'ru' | 'en'
                )
              )}`}
              onClick={() => modal.close()}
            />
          )}
          {isMainPage && (
            <Button
              as="button"
              className={styles.map__button}
              size="lg"
              theme="dark"
              disabled={quantity === 0}
              label={t('apply')}
              onClick={() => modal.close()}
            />
          )}
        </div>
        <div
          className={clsx(
            styles.map__right,
            isInAllFilters && styles['map__right--all'],
            selected === 1 && styles['map__right--samui']
          )}
        >
          {selected === 0 ? (
            <PhuketMapWithBeaches isShow={isShow} type="sale" />
          ) : (
            <SamuiMapWithBeaches isShow={isShow} type="sale" />
          )}
          <ToggleButton
            className={styles.map__show}
            as="button"
            size="md"
            theme="secondary"
            isActive={isShow ? false : true}
            label={isShow ? t('catalog.hideBeaches') : t('catalog.showBeaches')}
            onClick={() => toggleShowBeaches()}
          />
        </div>
      </div>
    </Tabs>
  );
};
